import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosAuth from "../axiosAuth";
import { FILE_UPLOAD, SCHOOL } from "./apiRoutes";

export interface UpdateSchoolPayload {
  schoolId: string;
  schoolName?: string;
  state?: string;
  city?: string;
  zip?: string;
  geoRegionId?: string | number | null;
  phoneNumber?: string;
  emailId?: string;
  website?: string;
  logo?: File | string | null;
  address1?: string;
  address2?: string;
  academicCollaboration?: string | number | null;
  gradesSelected?: string[];
  facultyDays?: string[];
  noOfFacultyDays?: string | null;
  educatorType?: string;
  maintenance?: string;
  sku?: string;
  noOfWorkstations?: string | number | null;
  includesBooks?: boolean | string;
  schoolTier?: string;
  syllabus?: string[];
  accountType: string;
  studentStrength?: number;
}

const uploadLogoAndGetUuid = async (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("file_type", "logo");
  const res = await axiosAuth.post(FILE_UPLOAD, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res?.data?.data?.attachment_id as string;
};

export const useUpdateSchoolMutation = (options?: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: async (payload: UpdateSchoolPayload) => {
      const {
        schoolId,
        logo,
        includesBooks,
        academicCollaboration,
        noOfWorkstations,
        noOfFacultyDays,
        ...rest
      } = payload as any;

      let logoUuid: string | undefined;
      if (logo instanceof File) {
        logoUuid = await uploadLogoAndGetUuid(logo);
      }

      const bodyWithNulls = {
        ...rest,
        academicCollaboration,
        noOfWorkstations,
        noOfFacultyDays,
        includesBooks:
          typeof includesBooks === "string"
            ? includesBooks
            : includesBooks === true
            ? "Yes"
            : includesBooks === false
            ? "No"
            : undefined,
        schoolLogo: logoUuid ?? (typeof logo === "string" ? logo : undefined),
      } as Record<string, unknown>;

      const body = Object.fromEntries(
        Object.entries(bodyWithNulls).filter(([, v]) => v !== null && v !== undefined)
      );

      const response = await axiosAuth.put(`${SCHOOL}${schoolId}/`, body);
      return response.data;
    },
    onSuccess: () => {
      toast.success("School updated successfully!");
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.issues?.message || error?.response?.data?.message || "Failed to update school";
      toast.error(message);
    },
  });
};


